import { useQuery } from '~/shared/hooks/query'
import { providersWorkUnitsInbox, ProvidersWorkUnitsInboxListResponse } from '@fireflyhealth/api'
import { formatRFC3339DateTime } from '~/shared/lib/date'

interface Params {
  ownerIds?: number[]
  ownerGroupIds?: number[]
  podIds?: number[]
  dueDateRange?: {
    start?: Date
    end?: Date
  }
  /**
   * `None` groups the care plans with no template: the ids are joined into
   * `careplan__autocreated_from_id__in`, and the backend reads that spelling back as a null
   * template rather than an id.
   */
  carePlanAutocreatedFromIds?: CarePlanTemplateId[]
  caseCategoryIds?: number[]
  limit: number
  offset: number
}

export type CarePlanTemplateId = number | 'None'

function mapParams(params: Params) {
  const contentTypeCounts: Record<string, number | undefined> = {
    careplan: params.carePlanAutocreatedFromIds?.length,
    case: params.caseCategoryIds?.length,
  }
  const contentTypeFilterList = ['careplan', 'case'].filter(ct => (contentTypeCounts[ct] ?? 0) > 0)

  return {
    sort_by: 'priority',
    owner_id__in: params.ownerIds?.length ? params.ownerIds.join(',') : undefined,
    owner_group_id__in: params.ownerGroupIds?.length ? params.ownerGroupIds.join(',') : undefined,
    pod_id__in: params.podIds?.length ? params.podIds.join(',') : undefined,
    due_date__gte: params.dueDateRange?.start
      ? formatRFC3339DateTime(params.dueDateRange.start)
      : undefined,
    due_date__lte: params.dueDateRange?.end
      ? formatRFC3339DateTime(params.dueDateRange.end)
      : undefined,
    content_type__in: contentTypeFilterList.length ? contentTypeFilterList.join(',') : undefined,
    careplan__autocreated_from_id__in: params.carePlanAutocreatedFromIds?.length
      ? params.carePlanAutocreatedFromIds.join(',')
      : undefined,
    case__category_id__in: params.caseCategoryIds?.length
      ? params.caseCategoryIds.join(',')
      : undefined,
    limit: params.limit,
    offset: params.offset,
  }
}

export const useInbox = (params: Params) => {
  const apiParams = mapParams(params)
  const queryKey = providersWorkUnitsInbox.listQueryKey({ provider_id: 'me' }, apiParams)

  const { data, isPending: isLoading } = useQuery(
    queryKey,

    async (): Promise<ProvidersWorkUnitsInboxListResponse> => {
      const response = await providersWorkUnitsInbox.list(
        { provider_id: 'me' },
        { params: apiParams },
      )
      return response.data
    },
    {},
    { error: 'Failed to fetch inbox' },
  )

  return { data, isLoading }
}
